import { Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { IGStoryReactionSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import {
  MOBILE_SMALL,
  MOBILE_THRESHOLD,
} from '../../../../constants/screenSize';
import { InstagramRespondRadioBtns } from '../../../../features/common/components/IGRespondRadioBtns/InstagramRespondRadioBtns';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IGStoryReactionSlideoutEditor.module.css';

export interface IGStoryReactionSlideoutEditorProps {
  promoterId: number;
}

export const IGStoryReactionSlideoutEditor = ({
  promoterId,
}: IGStoryReactionSlideoutEditorProps) => {
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const promoterCRUDActions = usePromoterCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();
  const { setRef } = useSlideoutOnClose();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const { screenWidth } = useResolutionInfo();
  const isMobile =
    screenWidth <= MOBILE_THRESHOLD && screenWidth > MOBILE_SMALL;
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  useEffect(() => {
    const fetchPromoters = async () => {
      const response = await promoterCRUDActions.retrieveAll(
        chatbotId,
        'instagram_story_reaction',
      );
      setPromoters(response);
    };
    fetchPromoters();
  }, [chatbotId, promoterCRUDActions]);

  useEffect(() => {
    (async () => {
      if (page) {
        await Promise.all([
          pageCRUDActions.retrieveById(page.id),
          widgetCRUDActions.retrieveAll(page.id, true),
        ]);
      }
    })();
  }, [page?.id, pageCRUDActions, widgetCRUDActions]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader promoter={promoter} title="Instagram Story Reaction" />
      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        {!isMobile && (
          <Banner
            css={{
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            withBannerHeader
            headerText="What is an Instagram Story Reaction Autoresponder?"
            withLeftArea={false}
            showCloseIcon
            usedInSlideout
          >
            <IGStoryReactionSlideoutBanner textColor="white" />
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Choose how to respond"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <InstagramRespondRadioBtns
                promoter={promoter}
                promoters={promoters}
                type="mentions"
                css={{
                  radioInputLabel: styles.igRadioInputLabel,
                  textLink: styles.textLink,
                }}
                showInfoIcon
                keywordInfoIconColor="#262626"
                customKeywordBroadPosition="bottom"
                customKeywordBroadAlign={screenWidth > 900 ? 'center' : 'end'}
              />
            </CollapsibleContentContainer>

            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Reply Instantly to users who react to your Stories "
              className={styles.collapsibleContainer}
              childrenContainerClassName={
                styles.collapsibleChildrenContainerLast
              }
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                editPaneClassname={styles.dialogueEditPane}
                previewClassName={styles.dialogueEditPreviewPane}
                dialogueId={page.id}
                widgetsToExclude={widgetsToExclude}
                platforms={['instagram']}
                usedInSlideout
                hideDialogueSelector
                showAttributesStartWarning
                forInstagram
              />
            </CollapsibleContentContainer>
          </div>
          {!isMobile && (
            <DialoguePreview
              dialogueId={page.id}
              className={styles.dialoguePreview}
              sticky
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
